/**
 * Reglas del checkout por pasos de la tienda online (spec 2026-10-19 §5), detrás
 * de `locations.features.checkout_steps_v2`. Puras y con tests: el componente
 * solo consume. Con el flag apagado nada de esto se usa.
 */
import type { Step } from '@/components/microsite/sheet/stepMachine'

/**
 * El checkout por pasos es el de TODAS las tiendas desde 2026-10-20 (decisión de
 * Samuel tras el piloto en Empanada Factory). `checkout_steps_v2: false` queda
 * como interruptor de emergencia por tienda, sin deploy, hasta que se borre el
 * camino viejo (cuando haya pedidos pagados reales por este camino).
 */
export function isCheckoutStepsV2(features: unknown): boolean {
  if (!features || typeof features !== 'object') return true
  return (features as Record<string, unknown>).checkout_steps_v2 !== false
}

/**
 * ¿Hay algo que elegir en "¿Cuándo?"? Con pedidos anticipados, salvo a
 * domicilio: no hay programados a domicilio todavía (decisión 2026-09-05; el
 * despacho a la hora del horario no está probado en producción). Envío sí
 * pasa por acá, como en el carrito de siempre (el picker sale para todo lo que
 * no es domicilio): con la tienda cerrada es lo que lo obliga a programar.
 */
export function whenNeeded(i: { schedulable: boolean; isDelivery: boolean; isShip: boolean }): boolean {
  return i.schedulable && !i.isDelivery
}

/**
 * Pasos que se saltan al avanzar. Siguen en la lista (stepMachine.ts) para que
 * "Cambiar" pueda entrar a ellos y Atrás/Siguiente funcionen desde ahí.
 */
export function skipSetV2(i: {
  hasFulfillmentChoice: boolean
  fulfillmentChosenInBar: boolean
  whenNeeded: boolean
  hasFreshQuote: boolean
  /** A domicilio elegido con la tienda cerrada: "¿Cómo?" NO se salta aunque
   *  se haya elegido en la barra. Es el único paso que lo frena (gateV2) y
   *  donde el cliente puede cambiar a recoger; saltarlo lo dejaría llegar a
   *  Pago con un domicilio inmediato que /session rechaza. */
  deliveryBlocked?: boolean
  /** La barra de la carta solo ofrece recoger/domicilio: con envío disponible,
   *  "¿Cómo?" no se salta (si no, el envío quedaría inalcanzable). */
  shippingAvailable?: boolean
}): ReadonlySet<Step> {
  const s = new Set<Step>()
  if (!i.hasFulfillmentChoice || (i.fulfillmentChosenInBar && !i.deliveryBlocked && !i.shippingAvailable)) s.add('fulfillment')
  if (!i.whenNeeded) s.add('when')
  if (i.hasFreshQuote) s.add('address')
  return s
}

export type GateReason = 'empty' | 'paused' | 'closed'
export type GateResult = { ok: boolean; reason: GateReason | null }

export interface GateState {
  isEmpty: boolean
  paused: boolean
  acceptingOrders: boolean
  schedulable: boolean
  isDelivery: boolean
  scheduleMode: 'asap' | 'scheduled'
  scheduledFor: string | null
}

const OK: GateResult = { ok: true, reason: null }
const no = (reason: GateReason): GateResult => ({ ok: false, reason })

/**
 * ¿Puede avanzar el paso? La pausa gana sobre todo (también sobre lo
 * programado, como hoy en CartStep). Con la tienda cerrada:
 *  - sin pedidos anticipados no se sale del carrito;
 *  - con anticipados, "¿Cuándo?" exige un horario (nunca "lo antes posible");
 *  - a domicilio no avanza (no hay programados a domicilio).
 */
export function gateV2(step: 'cart' | 'fulfillment' | 'when', s: GateState): GateResult {
  if (step === 'cart' && s.isEmpty) return no('empty')
  if (s.paused) return no('paused')
  if (step === 'cart') return !s.acceptingOrders && !s.schedulable ? no('closed') : OK
  if (step === 'fulfillment') return s.isDelivery && !s.acceptingOrders ? no('closed') : OK
  if (s.scheduleMode === 'scheduled') return s.scheduledFor ? OK : no('closed')
  return s.acceptingOrders ? OK : no('closed')
}

/** "A domicilio" va deshabilitado mientras la tienda no recibe pedidos ahora. */
export function deliveryDisabledWhenClosed(i: { acceptingOrders: boolean }): boolean {
  return !i.acceptingOrders
}
